// frontend/src/hooks/useP2POrderbook.ts
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api as apiClient, P2POrder, P2POrderbookSnapshot } from '../lib/api';
import { getOrderbookSocket, WS_EVENTS } from '../lib/p2p/socket';

const queryKey = (pair: string) => ['p2p', 'orderbook', pair] as const;

interface ConnectionState {
  connected: boolean;
}

/**
 * Subscribes to the live orderbook for `pair`. Returns the snapshot as
 * react-query state + a small connection indicator.
 *
 *   - First mount: REST snapshot → cache
 *   - WS connect: emit 'subscribe' → server pushes 'orderbook:snapshot'
 *     (replaces cache, authoritative)
 *   - Deltas: order_added / order_updated / order_removed mutate cache
 *   - Reconnect: re-emit subscribe + invalidate cache (REST refetch)
 *   - Unmount: emit 'unsubscribe', no disconnect (other components may share)
 */
export function useP2POrderbook(pair: string) {
  const queryClient = useQueryClient();
  const [conn, setConn] = useState<ConnectionState>({ connected: false });

  const query = useQuery({
    queryKey: queryKey(pair),
    queryFn: () => apiClient.getP2POrderbook(pair),
    staleTime: 5_000,
  });

  useEffect(() => {
    const socket = getOrderbookSocket();

    function applyUpsert(updated: Partial<P2POrder> & { orderId: string }) {
      queryClient.setQueryData<P2POrderbookSnapshot | undefined>(queryKey(pair), (prev) => {
        if (!prev) return prev;
        const existing = prev.orders.find((o) => o.orderId === updated.orderId);
        if (existing) {
          return {
            ...prev,
            orders: prev.orders.map((o) =>
              o.orderId === updated.orderId ? { ...o, ...updated } : o,
            ),
          };
        }
        // Unknown order via update → trigger a refetch as fallback
        queryClient.invalidateQueries({ queryKey: queryKey(pair) });
        return prev;
      });
    }

    function applyAdd(order: P2POrder) {
      queryClient.setQueryData<P2POrderbookSnapshot | undefined>(queryKey(pair), (prev) => {
        if (!prev) return prev;
        if (prev.orders.some((o) => o.orderId === order.orderId)) return prev;
        return { ...prev, orders: [...prev.orders, order] };
      });
    }

    function applyRemove(orderId: string) {
      queryClient.setQueryData<P2POrderbookSnapshot | undefined>(queryKey(pair), (prev) => {
        if (!prev) return prev;
        return { ...prev, orders: prev.orders.filter((o) => o.orderId !== orderId) };
      });
    }

    function applySnapshot(snap: P2POrderbookSnapshot) {
      queryClient.setQueryData(queryKey(pair), snap);
    }

    const onConnect = () => {
      setConn({ connected: true });
      socket.emit('subscribe', pair);
    };
    const onDisconnect = () => setConn({ connected: false });
    const onSnapshot = (snap: P2POrderbookSnapshot) => applySnapshot(snap);
    const onOrderAdded = (p: P2POrder) => applyAdd(p);
    const onOrderUpdated = (p: Partial<P2POrder> & { orderId: string }) => applyUpsert(p);
    const onOrderRemoved = (p: { orderId: string }) => applyRemove(p.orderId);
    const onTradeNew = () => {
      queryClient.invalidateQueries({ queryKey: ['p2p', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['p2p', 'trades', 'me'] });
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on(WS_EVENTS.SNAPSHOT, onSnapshot);
    socket.on(WS_EVENTS.ORDER_ADDED, onOrderAdded);
    socket.on(WS_EVENTS.ORDER_UPDATED, onOrderUpdated);
    socket.on(WS_EVENTS.ORDER_REMOVED, onOrderRemoved);
    socket.on(WS_EVENTS.TRADE_NEW, onTradeNew);

    if (socket.connected) onConnect();

    return () => {
      socket.emit('unsubscribe', pair);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off(WS_EVENTS.SNAPSHOT, onSnapshot);
      socket.off(WS_EVENTS.ORDER_ADDED, onOrderAdded);
      socket.off(WS_EVENTS.ORDER_UPDATED, onOrderUpdated);
      socket.off(WS_EVENTS.ORDER_REMOVED, onOrderRemoved);
      socket.off(WS_EVENTS.TRADE_NEW, onTradeNew);
    };
  }, [pair, queryClient]);

  return {
    ...query,
    orders: query.data?.orders ?? [],
    connected: conn.connected,
  };
}
